import { useEffect, useRef, useState } from 'react';
import type { Deal, Stage } from '@epasu/core';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EntityPicker } from '@/components/EntityPicker';

interface Props {
  /** null = neuer Deal */
  deal: Deal | null;
  /** Stufe, in der ein neuer Deal landet */
  stage: Stage;
  stages: Stage[];
  pipelineId: string;
  /**
   * Kampagne, aus deren Antwort der Deal entsteht — nur bei einem neuen Deal
   * gesetzt (etwa von der Antworten-Seite). Herkunft, keine laufende Bindung,
   * deshalb kein Feld im Formular selbst.
   */
  sourceCampaignId?: string | null;
  /** Vorbelegter Ansprechpartner bei einem neuen Deal, etwa von der Antworten-Seite. */
  defaultContactId?: string | null;
  onClose: () => void;
  /** bekommt die ID des gespeicherten Deals — der Aufrufer prüft die Stufe */
  onSaved: (dealId: string) => void;
}

/**
 * Anlegen und Bearbeiten eines Deals.
 *
 * Der Wert wird in Euro eingegeben und als Cent gespeichert — die Datenbank
 * rechnet durchgehend in Cent, um Rundungsfehler bei Gleitkommazahlen zu
 * vermeiden.
 *
 * Beim Bearbeiten lässt sich auch die Stufe wechseln. Das läuft dann über
 * `moveDeal`, nicht über `updateDeal`: nur dort hängen die Automations dran,
 * die etwa beim Wechsel auf „Gewonnen“ eine Aufgabe erzeugen.
 */
export function DealDialog({
  deal,
  stage,
  stages,
  pipelineId,
  sourceCampaignId,
  defaultContactId,
  onClose,
  onSaved,
}: Props) {
  const [title, setTitle] = useState(deal?.title ?? '');
  const [euro, setEuro] = useState(
    deal && deal.valueCents ? (deal.valueCents / 100).toFixed(2).replace('.', ',') : '',
  );
  const [contactId, setContactId] = useState(deal?.contactId ?? defaultContactId ?? '');
  const [closeDate, setCloseDate] = useState(deal?.expectedCloseDate?.slice(0, 10) ?? '');
  const [stageId, setStageId] = useState(deal?.stageId ?? stage.id);
  const [notes, setNotes] = useState(deal?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Bitte einen Titel angeben.');
      return;
    }

    const cents = parseEuro(euro);
    if (cents === null) {
      setError('Der Wert ist keine gültige Zahl.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      let gespeicherteId: string;
      if (deal) {
        await crm.pipelines.updateDeal(deal.id, {
          title: title.trim(),
          valueCents: cents,
          contactId: contactId || null,
          expectedCloseDate: closeDate || null,
          notes: notes.trim() || null,
        });
        // Stufenwechsel separat, damit die Automations laufen.
        if (stageId !== deal.stageId) {
          await crm.pipelines.moveDeal(deal.id, stageId);
        }
        gespeicherteId = deal.id;
      } else {
        // Die Firma kommt vom Kontakt, damit der Deal auch dann noch
        // zuzuordnen ist, wenn die Person später gelöscht wird.
        const gewaehlterKontakt = contactId ? await crm.contacts.get(contactId) : null;
        const angelegt = await crm.pipelines.createDeal({
          pipelineId,
          stageId,
          title: title.trim(),
          valueCents: cents,
          contactId: contactId || null,
          organizationId: gewaehlterKontakt?.organizationId ?? null,
          expectedCloseDate: closeDate || null,
          notes: notes.trim() || null,
          ...(sourceCampaignId ? { sourceCampaignId } : {}),
        });
        gespeicherteId = angelegt.id;
      }
      onSaved(gespeicherteId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={deal ? 'Deal bearbeiten' : 'Neuer Deal'}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-auto w-full max-w-md rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">
          {deal ? 'Deal bearbeiten' : 'Neuer Deal'}
        </h2>

        <div className="mt-4 space-y-4">
          <Field label="Titel">
            <Input
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ausweise Schuljahr 2026/27"
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Wert (€)">
              <Input
                inputMode="decimal"
                value={euro}
                onChange={(e) => setEuro(e.target.value)}
                placeholder="1250,00"
              />
            </Field>
            <Field label="Erwarteter Abschluss">
              <Input
                type="date"
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
              />
            </Field>
          </div>

          <Field label="Stufe">
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={stageId}
              onChange={(e) => setStageId(e.target.value)}
            >
              {stages.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Ansprechpartner">
            <EntityPicker
              kind="contact"
              value={contactId || null}
              onChange={(id) => setContactId(id ?? '')}
              placeholder="— keiner —"
            />
          </Field>

          <Field label="Notizen">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Absagegrund, Gesprächsstand, wann ein neuer Anlauf Sinn hat"
              className="w-full resize-y rounded-md border border-input bg-card px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </Field>
        </div>

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Speichere …' : 'Speichern'}
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * Euro-Eingabe in Cent. Akzeptiert Komma und Punkt sowie Tausenderpunkte, wie
 * sie beim Kopieren aus einer Tabelle anfallen. Gibt null bei Unsinn zurück.
 */
function parseEuro(input: string): number | null {
  const t = input.trim();
  if (!t) return 0;
  const normalisiert = t.replace(/\s/g, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.');
  const n = Number(normalisiert);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
